// ════════════════════════════════════════════════════════════════════════
// PROFILS DU FORMULAIRE DE CONTACT — source unique
// ════════════════════════════════════════════════════════════════════════
// POURQUOI CE FICHIER EXISTE (18/08/2026, lead Perspective Blue).
//
// La liste « Vous êtes » de la vitrine était un tableau plat de sept libellés
// qui mélangeait DEUX questions différentes sans jamais le dire :
//
//   — des profils de CLIENT   : indépendant, employeur direct
//   — des profils de CANAL    : fiduciaire, secrétariat social, courtier
//
// Un employeur qui CHERCHE un secrétariat social coche donc « Secrétariat
// social », ce qui est la lecture naturelle du libellé. C'est exactement ce
// qui est arrivé le 18/08 : un cabinet de recrutement d'Ixelles, employeur
// ONSS depuis 2023, est arrivé étiqueté « Secrétariat social ». Le lead part
// au mauvais traitement, et la réponse commerciale se prépare sur une
// hypothèse fausse — ici il a fallu ouvrir le site du prospect et la BCE pour
// s'en apercevoir.
//
// Le correctif tient en deux gestes :
//   1. GROUPER les options sous deux intitulés explicites (`<optgroup>`), pour
//      que le visiteur voie la question avant de voir les réponses ;
//   2. transmettre un CODE stable plutôt que le libellé traduit, pour que la
//      notification interne dise « Client · … » ou « Partenaire · … » quelle
//      que soit la langue du visiteur. Avant, un lead néerlandophone arrivait
//      en « Sociaal secretariaat », aussi ambigu et en plus à traduire.
//
// La valeur voyage jusqu'à `contact_consents.role` (Supabase) via
// `app/api/contact/route.js`. Les valeurs HISTORIQUES sont des libellés en
// clair dans les quatre langues : `resoudreProfil()` les reconnaît encore, on
// ne réécrit pas le passé et une page servie depuis un cache continue de
// fonctionner.
// ════════════════════════════════════════════════════════════════════════

/** Les deux familles. L'intitulé est ce que le visiteur lit AVANT les options. */
export const GROUPES_PROFIL = {
  client: {
    fr: 'Je cherche un prestataire pour ma société',
    nl: 'Ik zoek een dienstverlener voor mijn onderneming',
    en: 'I am looking for a provider for my company',
    de: 'Ich suche einen Dienstleister für mein Unternehmen',
    // Ce que la notification interne affiche, toujours en français.
    admin: 'Client',
  },
  partenaire: {
    fr: 'Je suis un partenaire ou un prescripteur',
    nl: 'Ik ben een partner of doorverwijzer',
    en: 'I am a partner or referrer',
    de: 'Ich bin Partner oder Vermittler',
    admin: 'Partenaire',
  },
};

/**
 * Les profils, dans l'ordre d'affichage.
 *
 * ⚠️ `code` est un identifiant STABLE : il part en base et sert aux statistiques.
 * On ne le renomme pas pour faire joli — on ajoute une entrée et on laisse
 * l'ancienne se faire reconnaître par `resoudreProfil()`.
 */
export const PROFILS_CONTACT = [
  // ── Famille CLIENT ────────────────────────────────────────────────────
  {
    code: 'client_independant', groupe: 'client',
    fr: 'Indépendant / Starter', nl: 'Zelfstandige / Starter',
    en: 'Freelancer / Starter', de: 'Selbständig / Starter',
  },
  {
    code: 'client_employeur', groupe: 'client',
    fr: 'Employeur — société ou ASBL', nl: 'Werkgever — vennootschap of vzw',
    en: 'Employer — company or non-profit', de: 'Arbeitgeber — Gesellschaft oder VoG',
  },
  {
    // L'option qui manquait le 18/08. Un prospect qui quitte son secrétariat
    // social n'avait, pour le dire, que la case « Secrétariat social ».
    code: 'client_change_prestataire', groupe: 'client',
    fr: 'Je quitte mon secrétariat social actuel',
    nl: 'Ik verlaat mijn huidige sociaal secretariaat',
    en: 'I am leaving my current payroll provider',
    de: 'Ich verlasse mein derzeitiges Sozialsekretariat',
  },
  // ── Famille PARTENAIRE ────────────────────────────────────────────────
  {
    code: 'partenaire_fiduciaire', groupe: 'partenaire',
    fr: 'Fiduciaire / Expert-comptable', nl: 'Fiduciaire / Accountant',
    en: 'Fiduciary / Accountant', de: 'Fiduziaire / Buchhalter',
  },
  {
    code: 'partenaire_secretariat', groupe: 'partenaire',
    fr: 'Secrétariat social', nl: 'Sociaal secretariaat',
    en: 'Social secretariat', de: 'Sozialsekretariat',
  },
  {
    code: 'partenaire_courtier', groupe: 'partenaire',
    fr: 'Courtier / Apporteur d’affaires', nl: 'Makelaar / Zakenaanbrenger',
    en: 'Broker / Referral partner', de: 'Makler / Geschäftsvermittler',
  },
  // ── Hors famille ──────────────────────────────────────────────────────
  { code: 'autre', groupe: null, fr: 'Autre', nl: 'Andere', en: 'Other', de: 'Andere' },
];

/** Libellé de l'option vide, par langue. Sa valeur transmise est '' (champ requis). */
export const PLACEHOLDER_PROFIL = {
  fr: 'Sélectionnez...', nl: 'Selecteer...', en: 'Select...', de: 'Auswählen...',
};

const LANGUES = ['fr', 'nl', 'en', 'de'];
const langueValide = (lang) => (LANGUES.includes(lang) ? lang : 'fr');

/**
 * Les profils prêts à rendre en `<optgroup>`, dans la langue demandée.
 * Les profils sans groupe (« Autre ») sortent dans une entrée `groupe: null`,
 * que le rendu place à plat, hors de tout intitulé.
 */
export function getGroupesProfil(lang = 'fr') {
  const l = langueValide(lang);
  const ordre = [];
  for (const p of PROFILS_CONTACT) {
    let bloc = ordre.find((b) => b.groupe === p.groupe);
    if (!bloc) {
      bloc = {
        groupe: p.groupe,
        label: p.groupe ? GROUPES_PROFIL[p.groupe][l] : null,
        options: [],
      };
      ordre.push(bloc);
    }
    bloc.options.push({ code: p.code, label: p[l] });
  }
  return ordre;
}

/**
 * Retrouve un profil depuis ce qui a été transmis.
 *
 * Accepte un code actuel OU un libellé historique dans n'importe laquelle des
 * quatre langues — c'est ce qui rend le changement rétro-compatible : les
 * lignes déjà en base et les pages encore en cache restent lisibles.
 * Rend `null` si la valeur n'est pas reconnue (jamais une exception : une
 * notification commerciale ne se perd pas pour un libellé inattendu).
 */
export function resoudreProfil(valeur) {
  if (!valeur || typeof valeur !== 'string') return null;
  const v = valeur.trim();
  if (!v) return null;
  const parCode = PROFILS_CONTACT.find((p) => p.code === v);
  if (parCode) return parCode;
  const vNorm = v.toLowerCase();
  return (
    PROFILS_CONTACT.find((p) =>
      LANGUES.some((l) => p[l].toLowerCase() === vNorm),
    ) || null
  );
}

/** Vrai si la valeur est l'intitulé de l'option vide, dans l'une des 4 langues. */
export function estPlaceholder(valeur) {
  if (!valeur || typeof valeur !== 'string') return false;
  const v = valeur.trim().toLowerCase();
  return LANGUES.some((l) => PLACEHOLDER_PROFIL[l].toLowerCase() === v);
}

/**
 * Ce qu'affiche la notification interne : « Client · Employeur — société ou ASBL ».
 * Toujours en français, comme le reste du tableau de la notification.
 * Une valeur inconnue est rendue TELLE QUELLE plutôt que masquée — on préfère
 * un libellé daté à un champ vide.
 */
export function libelleProfilAdmin(valeur) {
  const p = resoudreProfil(valeur);
  // Un visiteur servi depuis un cache peut encore poster le libellé de l'option
  // vide (« Sélectionnez... ») : ce n'est pas un profil, la ligne ne s'affiche pas.
  if (!p && estPlaceholder(valeur)) return '';
  if (!p) return typeof valeur === 'string' ? valeur.trim() : '';
  return p.groupe ? `${GROUPES_PROFIL[p.groupe].admin} · ${p.fr}` : p.fr;
}
